// DataTram Help & Support FAQs — source content for the General Support section.
// Kept as structured data (not i18n) because it is long-form help content, grouped
// by category. The shape mirrors the backend `/feedback/faqs` `category` field, so
// this can later be swapped for a categorized backend response with no UI rework.

export interface FaqEntry {
  question: string;
  /** Main answer text (shown first). */
  answer: string;
  /** Optional bullet list rendered under the answer. */
  bullets?: string[];
  /** Optional trailing paragraph shown after the bullets. */
  outro?: string;
}

export interface FaqCategory {
  id: string;
  /** Two-digit label matching the source document (e.g. "01"). */
  number: string;
  title: string;
  faqs: FaqEntry[];
}

export const FAQ_CATEGORIES: FaqCategory[] = [
  {
    id: "getting-started",
    number: "01",
    title: "Getting Started",
    faqs: [
      {
        question: "What is DataTram?",
        answer:
          "DataTram is an AI-powered marketing intelligence platform that brings your sales, website, advertising, organic social, customer, inventory, and competitor data into one place.",
      },
      {
        question: "Who is DataTram designed for?",
        answer:
          "DataTram is designed for e-commerce brands, retailers, marketing teams, performance marketers, founders, business managers, and agencies that want simpler, faster access to marketing and sales insights.",
      },
      {
        question: "What dashboards are available in DataTram?",
        answer:
          "Depending on your subscription and connected sources, DataTram may include Sales & Product Analytics, Geo-Spatial Analytics, Web Analytics, Organic Media, Paid Media, Customer Lifetime Value, Email Engagement, and Social Listening.",
      },
      {
        question: "Which data sources can I connect?",
        answer:
          "The current interface supports core integrations such as Shopify, Google Analytics, Google Ads, Facebook, and Instagram. Additional integrations may be enabled according to your account configuration.",
      },
      {
        question: "Where can I see my connected platforms?",
        answer:
          "Open Sources from the left navigation menu. The Connected Data Sources screen shows the platforms currently linked to your DataTram account.",
      },
      {
        question: "Do I need technical or BI expertise to use DataTram?",
        answer:
          "No. DataTram is designed for business users and provides prebuilt dashboards, AI-generated explanations, filters, tooltips, and natural-language querying.",
      },
      {
        question: "How do I change the reporting period?",
        answer:
          "Use the start-date and end-date selectors at the top of the dashboard. The dashboard metrics, charts, comparisons, and AI Executive Summary will update for the selected period.",
      },
      {
        question: "What does \"vs previous period\" mean?",
        answer:
          "It compares the selected date range with the immediately preceding equivalent period. For example, a selected 30-day period is compared with the previous 30 days.",
      },
      {
        question: "What do the green and red percentages mean?",
        answer:
          "The percentage shows how a metric changed compared with the previous period. The color indicates whether the movement is generally favourable or unfavourable for that metric. For example, an increase in sales may appear green, while a decrease in bounce rate may also appear green.",
      },
      {
        question: "What does the question-mark icon beside a metric mean?",
        answer:
          "Select or hover over the icon to view the metric's definition, calculation, or additional context.",
      },
      {
        question: "How often is my data refreshed?",
        answer:
          "DataTram refreshes connected data automatically according to the schedule configured for your account and data source. Refresh frequency may differ by integration.",
      },
      {
        question: "Why is a dashboard showing blank charts or zero values?",
        answer: "This can happen when:",
        bullets: [
          "No data exists for the selected date range",
          "The connected platform returned no activity",
          "A filter removed all matching records",
          "The source connection requires attention",
          "Data is still being refreshed",
        ],
        outro: "Try expanding the date range, clearing filters, and checking the Sources screen.",
      },
    ],
  },
  {
    id: "sales-product",
    number: "02",
    title: "Sales & Product Analytics",
    faqs: [
      {
        question: "What does the Sales & Product Analytics dashboard show?",
        answer:
          "It provides a consolidated view of Shopify sales and product performance, including sales, refunds, orders, average order value, customers, sales channels, product performance, inventory, and customer behaviour.",
      },
      {
        question: "Where does the Sales & Product Analytics data come from?",
        answer: "The dashboard primarily uses data from the connected Shopify store.",
      },
      {
        question: "What is included in the Overview tab?",
        answer: "The Overview tab provides a high-level summary of:",
        bullets: [
          "Total sales",
          "Refunds",
          "Orders",
          "Average order value",
          "Customers who ordered",
          "Sales trends",
          "Sales by channel",
          "Top-performing products",
        ],
      },
      {
        question: "What is included in the Sales tab?",
        answer: "The Sales tab provides deeper order and revenue analysis, including:",
        bullets: [
          "Total sales",
          "Top sales channel",
          "Fulfilled and unfulfilled orders",
          "Sales by channel",
          "Order-level information",
          "Campaign or channel contribution",
        ],
      },
      {
        question: "Can I review Shopify orders without opening Shopify?",
        answer:
          "Yes. DataTram can display order-level information, including the order status and available customer and transaction details.",
      },
      {
        question: "How can I find a specific order?",
        answer:
          "Use the available search and filters to find orders by information such as order number or customer name. You can also filter orders by fulfilment status.",
      },
      {
        question: "Can I export order information?",
        answer:
          "Yes. Export options are available on supported tables and reports. The format may vary depending on the screen and report.",
      },
      {
        question: "What does \"Sales by Channel\" mean?",
        answer:
          "Sales by Channel estimates which source contributed to an order, such as Google, Facebook, Instagram, Bing, direct traffic, or another source.",
      },
      {
        question: "Why might DataTram's channel attribution differ from Shopify?",
        answer:
          "DataTram may classify orders using referral information and source-matching rules. This may produce different channel assignments from the classifications used directly inside Shopify.",
      },
      {
        question: "What does the Inventory tab show?",
        answer: "The Inventory tab shows:",
        bullets: [
          "Total active products",
          "Products in stock",
          "Products with low stock",
          "Products that are out of stock",
          "Top products by on-hand quantity",
          "Product-level stock counts and statuses",
        ],
      },
      {
        question: "How do I change the low-stock threshold?",
        answer:
          "Enter the required quantity in the Low Stock Threshold field and confirm the change. Products at or below that quantity will be classified as low stock.",
      },
      {
        question: "Can each business use a different low-stock threshold?",
        answer: "Yes. The threshold can be adjusted based on the inventory requirements of your business.",
      },
      {
        question: "How can I find a particular product?",
        answer:
          "Use the product search field and available category or stock-status filters to find matching products.",
      },
      {
        question: "What do the inventory statuses mean?",
        answer: "Typical inventory statuses include:",
        bullets: [
          "In Stock: Inventory is above the configured low-stock threshold.",
          "Low Stock: Inventory is at or below the threshold but remains available.",
          "Out of Stock: No available inventory remains.",
        ],
      },
      {
        question: "What does the Customers tab show?",
        answer: "The Customers tab provides information such as:",
        bullets: [
          "Customers active during the selected period",
          "Customers who placed orders",
          "Average order value",
          "New and returning customers",
          "Customer purchase history",
          "Customer-level segments",
          "Total orders and total spend",
        ],
      },
      {
        question: "What is the difference between a new and returning customer?",
        answer:
          "A new customer placed their first order during the selected period. A returning customer had placed at least one order before the selected period and ordered again.",
      },
      {
        question: "How does DataTram segment customers?",
        answer:
          "DataTram uses customer behaviour such as recency, frequency, and monetary value to group customers into actionable segments. Examples may include loyal customers, high spenders, one-time buyers, idle customers, and customers at risk of churn.",
      },
      {
        question: "Can I export customer data?",
        answer:
          "Yes. Supported customer tables and audience lists can be exported using the available download option.",
      },
    ],
  },
  {
    id: "geo-spatial",
    number: "03",
    title: "Geo-Spatial Analytics",
    faqs: [
      {
        question: "What is the Geo-Spatial Analytics dashboard?",
        answer:
          "It shows how sales, orders, customers, discounts, quantities, and returns vary by geographic location.",
      },
      {
        question: "How is geographic information determined?",
        answer:
          "DataTram uses available order-location information to associate transactions with cities, provinces, states, or regions.",
      },
      {
        question: "What information appears for each region?",
        answer: "Depending on the available data, each region may show:",
        bullets: [
          "Total sales",
          "Orders",
          "Net quantity",
          "Discounts",
          "Returns",
          "Return orders",
          "Top-performing products",
        ],
      },
      {
        question: "What do the pins on the map show?",
        answer:
          "Selecting or hovering over a map location can show city-level information such as sales, orders, customers, and returns.",
      },
      {
        question: "How can the time heat map help me?",
        answer:
          "The heat map identifies the days and times when customers are most likely to place orders. This can help you schedule campaigns and allocate advertising spend more effectively.",
      },
      {
        question: "Can I filter the Geo-Spatial dashboard by city or region?",
        answer:
          "Yes. Available location and date filters can be used to focus the dashboard on a particular city, province, state, or region.",
      },
    ],
  },
  {
    id: "web-analytics",
    number: "04",
    title: "Web Analytics",
    faqs: [
      {
        question: "Where does the Web Analytics data come from?",
        answer: "The Web Analytics dashboard uses data from the connected Google Analytics account.",
      },
      {
        question: "What metrics are available in Web Analytics?",
        answer: "Metrics may include:",
        bullets: [
          "Total users",
          "Sessions",
          "Bounce rate",
          "Sessions per user",
          "Session duration",
          "Page views",
          "Revenue",
          "Key event rate",
        ],
      },
      {
        question: "Can I compare two months?",
        answer:
          "Yes. Use the two month selectors on the Performance Comparison screen to compare website performance across separate periods.",
      },
      {
        question: "Can I analyse website activity by location?",
        answer:
          "Yes. Available views may show users, sessions, page views, engagement, and bounce rate by city or other geographic dimensions.",
      },
      {
        question: "Can I analyse performance by demographic group?",
        answer:
          "Where supported by the connected data, DataTram can show performance by dimensions such as age, gender, and device.",
      },
      {
        question: "What is Channel Performance?",
        answer:
          "Channel Performance shows how website users and activity are distributed across acquisition channels, such as organic search, paid traffic, social traffic, direct traffic, or referrals.",
      },
    ],
  },
  {
    id: "organic-media",
    number: "05",
    title: "Organic Media Analytics",
    faqs: [
      {
        question: "What does the Organic Media dashboard cover?",
        answer:
          "It provides reporting for connected Facebook and Instagram pages, including content performance and follower insights.",
      },
      {
        question: "What is included in the Facebook Content Report?",
        answer: "The report may include:",
        bullets: [
          "Posts created",
          "Organic reach",
          "Impressions",
          "Clicks",
          "CTR",
          "Engagement rate",
          "Shares",
          "Comments",
          "Reactions",
          "Post-level performance",
        ],
      },
      {
        question: "What is included in the Instagram Content Report?",
        answer: "The report may include:",
        bullets: [
          "Posts created",
          "Reach and impressions",
          "Engagement rate",
          "Comments and saves",
          "Reactions",
          "Profile visits",
          "New followers",
          "Post-level performance",
        ],
      },
      {
        question: "What does Content Type Breakdown show?",
        answer:
          "It compares the number and performance of different formats, such as images, videos, reels, and carousels.",
      },
      {
        question: "Can I see the performance of an individual post?",
        answer:
          "Yes. The post-level table provides available metrics for individual posts and may include a link to open the original post.",
      },
      {
        question: "What do the follower reports show?",
        answer: "Follower reports may include:",
        bullets: [
          "Total followers",
          "New followers",
          "Follower growth trends",
          "Followers by country or city",
          "Age and gender distribution",
        ],
      },
    ],
  },
  {
    id: "paid-media",
    number: "06",
    title: "Paid Media Analytics",
    faqs: [
      {
        question: "Which advertising platforms are covered?",
        answer:
          "The Paid Media dashboard provides dedicated reporting for connected Meta Ads and Google Ads accounts.",
      },
      {
        question: "What does the Meta Ads dashboard show?",
        answer: "It may include:",
        bullets: [
          "Total spend",
          "Impressions",
          "Link clicks",
          "Purchases",
          "Click-through rate",
          "Cost per click",
          "Cost per purchase",
          "Purchase ROAS",
          "Conversion funnel",
          "Campaign, ad-set, and ad-level performance",
        ],
      },
      {
        question: "What does the Google Ads dashboard show?",
        answer: "It may include:",
        bullets: [
          "Advertising cost",
          "Impressions",
          "Clicks",
          "Conversions",
          "CTR",
          "CPC",
          "CPA",
          "ROAS",
          "Campaign and ad-group performance",
        ],
      },
      {
        question: "What does the Conversion Funnel show?",
        answer:
          "The funnel visualises how users progress from advertising spend to impressions, clicks, and conversions. It helps identify where the largest drop-offs occur.",
      },
      {
        question: "Can I switch individual trend metrics on or off?",
        answer:
          "Yes. Where metric selectors are available, you can choose which measures - such as spend, impressions, clicks, or conversions - appear in the chart.",
      },
      {
        question: "Can I compare performance across Meta placements?",
        answer:
          "Where the data is available, DataTram can compare performance across placements such as Facebook, Instagram, Audience Network, and other supported Meta placements.",
      },
      {
        question: "Can I review campaign, ad-set, and ad-group details?",
        answer:
          "Yes. Detailed tables allow users to drill from campaigns into lower-level structures such as Meta ad sets or Google ad groups.",
      },
    ],
  },
  {
    id: "cltv",
    number: "07",
    title: "Customer Lifetime Value and Predictive Analytics",
    faqs: [
      {
        question: "What is the CLTV dashboard?",
        answer:
          "The Customer Lifetime Value dashboard estimates customer value, retention behaviour, predicted revenue, and churn risk.",
      },
      {
        question: "What is Customer Lifetime Value?",
        answer:
          "Customer Lifetime Value estimates the total value a customer is expected to generate over their relationship with the business.",
      },
      {
        question: "What is churn risk?",
        answer:
          "Churn risk estimates the likelihood that a customer may stop purchasing. Customers may be grouped into high-, medium-, or low-risk categories.",
      },
      {
        question: "What is predicted revenue?",
        answer:
          "Predicted revenue is an estimate of future sales based on historical customer behaviour and available predictive models.",
      },
      {
        question: "What does Segment Evolution show?",
        answer:
          "Segment Evolution shows how customer groups - such as loyal customers, high spenders, idle customers, or one-time buyers - change over time.",
      },
      {
        question: "Can I target customers based on CLTV or churn?",
        answer:
          "Yes. Customer segments and predictive insights can be used to create audience lists for retention, reactivation, loyalty, or upsell campaigns.",
      },
    ],
  },
  {
    id: "ai-summary",
    number: "08",
    title: "AI Executive Summary",
    faqs: [
      {
        question: "What is the AI Executive Summary?",
        answer:
          "The AI Executive Summary converts dashboard data into a plain-language explanation of key changes, insights, risks, and recommended actions.",
      },
      {
        question: "Why does the AI Executive Summary change when I change the date?",
        answer:
          "The summary is generated using the data currently displayed on the dashboard. Changing the reporting period changes the underlying metrics and comparisons.",
      },
      {
        question: "How do I view the complete summary?",
        answer: "Select See more or use the expand arrow in the summary panel.",
      },
      {
        question: "Why is the AI Executive Summary still loading?",
        answer:
          "The summary may still be processing the selected data. Wait briefly and refresh the dashboard if it does not appear.",
      },
      {
        question: "What should I do if the summary is unavailable?",
        answer: "Check that:",
        bullets: [
          "The dashboard contains data",
          "The selected date range is valid",
          "The underlying source is connected",
          "The data refresh has completed",
        ],
      },
      {
        question: "Should I treat AI recommendations as final decisions?",
        answer:
          "AI recommendations should be used as decision support. Review important financial, operational, or campaign decisions against the underlying dashboard data and your business context.",
      },
    ],
  },
  {
    id: "ask-datatram",
    number: "09",
    title: "Ask DataTram",
    faqs: [
      {
        question: "What is Ask DataTram?",
        answer:
          "Ask DataTram is a conversational analytics assistant that lets you ask questions about your connected dashboards and data in plain English.",
      },
      {
        question: "What kinds of questions can I ask?",
        answer: "Examples include:",
        bullets: [
          "What were my total sales in the last 30 days?",
          "Which channel generated the most revenue?",
          "Which campaigns had the highest ROAS?",
          "Which customers are likely to churn?",
          "Which products are running low on stock?",
          "How did website performance change this month?",
        ],
      },
      {
        question: "Can I upload a dashboard screenshot?",
        answer:
          "Yes. You can attach a supported image and ask DataTram to explain the trends, metrics, or potential issues shown in the screenshot.",
      },
      {
        question: "Which attachment formats are supported?",
        answer:
          "The current interface supports common image formats such as JPEG, JPG, and PNG. Upload limits may apply.",
      },
      {
        question: "Can Ask DataTram help create a campaign?",
        answer:
          "Yes. Ask DataTram can assist with generating a campaign concept, creating email content, or preparing a target audience based on your request.",
      },
      {
        question: "Can Ask DataTram create a customer list?",
        answer:
          "Yes. You can describe the customers you want to target, such as customers who have not ordered recently, and DataTram can help prepare a matching segment where the required data is available.",
      },
      {
        question: "Is my chat history saved?",
        answer:
          "Your previous conversations may be retained within the Ask DataTram interface so you can continue earlier analysis.",
      },
      {
        question: "What does \"Clear Chat\" do?",
        answer:
          "It removes the messages currently displayed in the conversation. Only use it when you no longer need the current chat context.",
      },
      {
        question: "How can I get a better answer from Ask DataTram?",
        answer: "Include:",
        bullets: [
          "The metric you want",
          "The required period",
          "Any channel, product, campaign, customer, or location filter",
          "The type of output you need",
        ],
        outro:
          "For example: \"Compare Meta and Google Ads ROAS for June and explain the three biggest changes.\"",
      },
    ],
  },
  {
    id: "engagements",
    number: "10",
    title: "Engagements and Email Campaigns",
    faqs: [
      {
        question: "What is the Engagements module?",
        answer:
          "The Engagements module helps you create audiences, generate email campaigns, send targeted communications, and monitor campaign performance.",
      },
      {
        question: "What metrics appear in the Engagement Overview?",
        answer: "The overview may show:",
        bullets: [
          "Emails sent",
          "Emails opened",
          "Clicks",
          "Conversions",
          "Open rate",
          "Campaign-level performance trends",
        ],
      },
      {
        question: "How do I create a new campaign?",
        answer:
          "Open Engagements > Campaigns and select Create New Campaign. Follow the steps to choose an audience, prepare the email, review it, and schedule or send it.",
      },
      {
        question: "Can DataTram generate an email using AI?",
        answer:
          "Yes. DataTram can generate campaign content based on your audience, objective, offer, and business context.",
      },
      {
        question: "Can I edit an AI-generated email before sending it?",
        answer:
          "Yes. AI-generated emails should be reviewed in the email editor before they are approved or sent.",
      },
      {
        question: "What is the Audience screen?",
        answer:
          "The Audience screen contains customer lists and segments that can be used for targeted campaigns.",
      },
      {
        question: "How do I create a list or segment?",
        answer:
          "Open Engagements > Audience and select Create List / Segment. You can create a list manually or define customers using behavioural and business criteria.",
      },
      {
        question: "What is the difference between a list and a Data Science segment?",
        answer:
          "A list is generally created manually or through selected rules. A Data Science segment is generated using customer behaviour or predictive analysis, such as churn likelihood.",
      },
      {
        question: "Can I send emails to customers likely to churn?",
        answer:
          "Yes. Where a churn segment is available, it can be used as the target audience for a retention or reactivation campaign.",
      },
      {
        question: "Does DataTram currently support WhatsApp or SMS campaigns?",
        answer:
          "The current engagement module focuses on email. WhatsApp and SMS engagement may be introduced as additional channels.",
      },
    ],
  },
  {
    id: "social-listening",
    number: "11",
    title: "Social Listening Agent",
    faqs: [
      {
        question: "What is the Social Listening Agent?",
        answer:
          "It is an AI-powered tool for analysing brand and competitor conversations across supported digital platforms.",
      },
      {
        question: "Can I analyse my own brand?",
        answer:
          "Yes. You can use the Social Listening Agent to understand sentiment, recurring themes, customer feedback, and conversations relating to your own brand.",
      },
      {
        question: "Can I analyse competitors?",
        answer:
          "Yes. The agent can compare competitor activity, customer sentiment, strengths, weaknesses, and recurring discussion themes.",
      },
      {
        question: "Which platforms can be analysed?",
        answer:
          "Available sources may include Facebook, Instagram, TikTok, reviews, and other supported public channels. Coverage depends on the selected brand, region, and available data.",
      },
      {
        question: "What does sentiment analysis mean?",
        answer:
          "Sentiment analysis groups relevant comments or mentions into categories such as positive, negative, or neutral.",
      },
      {
        question: "Can I see the source behind an insight?",
        answer:
          "Where available, DataTram provides supporting links or references so you can review the original post, comment, or source.",
      },
      {
        question: "Can I analyse different countries or regions?",
        answer: "Yes. The Social Listening Agent supports regional analysis where relevant data is available.",
      },
    ],
  },
  {
    id: "notifications",
    number: "12",
    title: "Notifications",
    faqs: [
      {
        question: "What are DataTram notifications?",
        answer:
          "Notifications automatically alert you when an important metric changes or crosses a threshold that you have configured.",
      },
      {
        question: "Which types of metrics can I monitor?",
        answer: "Depending on the dashboard and account configuration, you may monitor areas such as:",
        bullets: [
          "Sales performance",
          "Advertising performance",
          "Website performance",
          "Organic social performance",
          "Inventory",
          "Customer activity",
        ],
      },
      {
        question: "How can notifications be delivered?",
        answer: "Available delivery methods may include:",
        bullets: ["In-app notifications", "Browser push notifications", "Email notifications"],
      },
      {
        question: "How do I configure a notification threshold?",
        answer:
          "Open Account Settings > Notification Preferences, select the metric, define the condition or threshold, choose the delivery channels, and enable the alert.",
      },
      {
        question: "Can I create an alert for a specific city or product?",
        answer:
          "Where supported, alerts can be scoped to dimensions such as a city, product, campaign, or channel.",
      },
      {
        question: "Where can I view previous notifications?",
        answer:
          "Open Account Settings > All Notifications. You can view notifications for today, this week, last week, or a custom date range.",
      },
      {
        question: "Why does the Notifications screen say no notifications are available?",
        answer:
          "No configured alert may have been triggered during the selected period. Try another date range or review your Notification Preferences.",
      },
      {
        question: "Can I disable a notification?",
        answer: "Yes. Open Notification Preferences and turn off the relevant rule or delivery channel.",
      },
    ],
  },
  {
    id: "user-management",
    number: "13",
    title: "User Management and Account Settings",
    faqs: [
      {
        question: "Who can manage users?",
        answer:
          "Users with the appropriate administrative access can add, edit, activate, deactivate, or remove users.",
      },
      {
        question: "How do I add a user?",
        answer:
          "Open User Management and select Add User. Enter the user's details and assign the appropriate access level.",
      },
      {
        question: "How many users are included?",
        answer:
          "Each tenant is configured with a default user allowance. The transcript indicates five users by default, while additional seats may depend on the subscription plan.",
      },
      {
        question: "What access levels are available?",
        answer: "The current interface includes access levels such as:",
        bullets: [
          "Admin: Can manage account-level settings and users.",
          "Viewer: Can access permitted dashboards and reports without administrative control.",
        ],
      },
      {
        question: "How do I change a user's access level?",
        answer:
          "Open User Management and select the required role from the Access Level dropdown beside the user.",
      },
      {
        question: "Can I temporarily deactivate a user?",
        answer:
          "Yes. Use the status toggle to deactivate the account without immediately deleting the user.",
      },
      {
        question: "How do I update my profile?",
        answer:
          "Open Account Settings > My Account. You can update supported profile details such as your name, website, industry, contact details, and profile photo.",
      },
      {
        question: "How do I change my password?",
        answer:
          "Open Account Settings > My Account, enter your current password, set and confirm the new password, and select Update Password.",
      },
    ],
  },
  {
    id: "troubleshooting",
    number: "14",
    title: "Troubleshooting",
    faqs: [
      {
        question: "What should I do if a connected source stops updating?",
        answer:
          "Open the Sources screen and check the connection. The integration may require reconnection, renewed permissions, or assistance from your administrator.",
      },
      {
        question: "Why does DataTram show a different value from the original platform?",
        answer: "Differences can be caused by:",
        bullets: [
          "Time-zone settings",
          "Date boundaries",
          "Attribution rules",
          "Currency handling",
          "Data refresh timing",
          "Platform-specific metric definitions",
          "Refund, cancellation, or fulfilment treatment",
        ],
        outro: "Confirm that the same dates, filters, account, and metric definitions are being compared.",
      },
      {
        question: "Why is recently generated platform data missing?",
        answer:
          "Some source platforms take time to finalise or expose data through their APIs. Allow the configured refresh to complete before comparing the latest activity.",
      },
      {
        question: "Why can another user see a feature that I cannot?",
        answer:
          "Feature visibility may depend on your role, subscription, tenant configuration, or connected data sources. Contact your DataTram administrator to review your access.",
      },
      {
        question: "What should I include when reporting a problem?",
        answer: "Provide:",
        bullets: [
          "The dashboard or page name",
          "The selected date range",
          "The affected metric",
          "Any filters applied",
          "A screenshot",
          "The expected and displayed values",
          "The approximate time the issue occurred",
        ],
      },
      {
        question: "How do I contact support?",
        answer:
          "Use the in-app help widget in the lower-right corner of the screen or contact your organisation's DataTram administrator.",
      },
    ],
  },
];
